import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../../components/layout/DashboardLayout';
import Card from '../../components/ui/Card';
import { ouvrirUseCase } from '../../utils/openUseCase';
import { 
  FileText, 
  Mail, 
  MessageSquare, 
  Calendar, 
  Presentation, 
  Newspaper,
  FileCheck,
  Briefcase,
  Award,
  Globe,
  BookOpen,
  Plus,
  Search,
  Filter
} from 'lucide-react';
import { settingsService } from '../../services/api/settings';
import LoadingSpinner from '../../components/ui/LoadingSpinner';

const iconMap: { [key: string]: React.ElementType } = {
  FileText,
  Mail,
  MessageSquare,
  Calendar,
  Presentation,
  Newspaper,
  FileCheck,
  Briefcase,
  Award,
  Globe,
  BookOpen,
  Plus
};

const FreeAssistedPage: React.FC = () => {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [defaultTemplates] = useState([
    {
      id: '1',
      title: 'Document Standard',
      description: 'Créer un document professionnel standard avec des sections personnalisables',
      icon: FileText,
      color: 'blue',
      link: '/workspace/redaction?template=standard'
    },
    {
      id: '2',
      title: 'Email Formel',
      description: 'Rédiger un email professionnel structuré et efficace',
      icon: Mail,
      color: 'green',
      link: '/workspace/redaction?template=email'
    },
    {
      id: '3',
      title: 'Message Courte',
      description: 'Créer des messages courts et percutants pour les réseaux sociaux',
      icon: MessageSquare,
      color: 'purple',
      link: '/workspace/redaction?template=message'
    },
    {
      id: '4',
      title: 'Planning',
      description: 'Organiser un planning détaillé avec dates et événements',
      icon: Calendar,
      color: 'orange',
      link: '/workspace/redaction?template=planning'
    },
    {
      id: '5',
      title: 'Présentation',
      description: 'Rédiger le contenu d\'une présentation PowerPoint',
      icon: Presentation,
      color: 'red',
      link: '/workspace/redaction?template=presentation'
    },
    {
      id: '6',
      title: 'Article de Blog',
      description: 'Créer un article de blog engageant et optimisé SEO',
      icon: Newspaper,
      color: 'teal',
      link: '/workspace/redaction?template=blog'
    },
    {
      id: '7',
      title: 'Rapport d\'Audit',
      description: 'Rédiger un rapport d\'audit professionnel et structuré',
      icon: FileCheck,
      color: 'indigo',
      link: '/workspace/redaction?template=audit'
    },
    {
      id: '8',
      title: 'Proposition Commerciale',
      description: 'Créer une proposition commerciale persuasive',
      icon: Briefcase,
      color: 'pink',
      link: '/workspace/redaction?template=proposal'
    },
    {
      id: '9',
      title: 'Certificat',
      description: 'Générer un certificat officiel professionnel',
      icon: Award,
      color: 'yellow',
      link: '/workspace/redaction?template=certificate'
    },
    {
      id: '10',
      title: 'Contenu Web',
      description: 'Rédiger du contenu web optimisé pour la conversion',
      icon: Globe,
      color: 'cyan',
      link: '/workspace/redaction?template=web'
    },
    {
      id: '11',
      title: 'Guide',
      description: 'Créer un guide complet étape par étape',
      icon: BookOpen,
      color: 'amber',
      link: '/workspace/redaction?template=guide'
    },
    {
      id: '12',
      title: 'Personnalisé',
      description: 'Créer votre propre modèle personnalisé',
      icon: Plus,
      color: 'slate',
      link: '/workspace/redaction'
    }
  ]);

  // Charger les use cases depuis l'API
  useEffect(() => {
    const loadUseCases = async () => {
      try {
        console.log('📋 Chargement des use cases dynamiques...');
        const useCasesData = await settingsService.getUseCases({ context: 'free_assisted' });
        console.log('✅ Use cases chargés:', useCasesData.length);
        
        const transformedTemplates = useCasesData.map((uc: any) => ({
            ...uc,
          color: uc.category_color || getColorFromCategory(uc.category_code || uc.category)
          }));
        
        setTemplates(transformedTemplates.length > 0 ? transformedTemplates : defaultTemplates);
      } catch (error) {
        console.error('❌ Erreur chargement use cases:', error);
        // Utiliser les templates par défaut en cas d'erreur
        setTemplates(defaultTemplates);
      } finally {
        setLoading(false);
      }
    };
    
    loadUseCases();
  }, []);

  // Fonction pour mapper les catégories aux couleurs
  const getColorFromCategory = (category: string): string => {
    const categoryColorMap: { [key: string]: string } = {
      redaction: 'blue',
      infographie: 'purple',
      visuel: 'teal',
      presentation: 'red',
      video: 'orange',
      audio: 'cyan'
    };
    return categoryColorMap[category] || 'slate';
  };

  const getColorClasses = (color: string) => {
    const colorMap: { [key: string]: { bg: string; border: string; text: string; hover: string } } = {
      blue: {
        bg: 'bg-blue-50',
        border: 'border-blue-200',
        text: 'text-blue-600',
        hover: 'hover:bg-blue-100 hover:border-blue-300'
      },
      green: {
        bg: 'bg-green-50',
        border: 'border-green-200',
        text: 'text-green-600',
        hover: 'hover:bg-green-100 hover:border-green-300'
      },
      purple: {
        bg: 'bg-purple-50',
        border: 'border-purple-200',
        text: 'text-purple-600',
        hover: 'hover:bg-purple-100 hover:border-purple-300'
      },
      orange: {
        bg: 'bg-orange-50',
        border: 'border-orange-200',
        text: 'text-orange-600',
        hover: 'hover:bg-orange-100 hover:border-orange-300'
      },
      red: {
        bg: 'bg-red-50',
        border: 'border-red-200',
        text: 'text-red-600',
        hover: 'hover:bg-red-100 hover:border-red-300'
      },
      teal: {
        bg: 'bg-teal-50',
        border: 'border-teal-200',
        text: 'text-teal-600',
        hover: 'hover:bg-teal-100 hover:border-teal-300'
      },
      indigo: {
        bg: 'bg-indigo-50',
        border: 'border-indigo-200',
        text: 'text-indigo-600',
        hover: 'hover:bg-indigo-100 hover:border-indigo-300'
      },
      pink: {
        bg: 'bg-pink-50',
        border: 'border-pink-200',
        text: 'text-pink-600',
        hover: 'hover:bg-pink-100 hover:border-pink-300'
      },
      yellow: {
        bg: 'bg-yellow-50',
        border: 'border-yellow-200',
        text: 'text-yellow-600',
        hover: 'hover:bg-yellow-100 hover:border-yellow-300'
      },
      cyan: {
        bg: 'bg-cyan-50',
        border: 'border-cyan-200',
        text: 'text-cyan-600',
        hover: 'hover:bg-cyan-100 hover:border-cyan-300'
      },
      amber: {
        bg: 'bg-amber-50',
        border: 'border-amber-200',
        text: 'text-amber-600',
        hover: 'hover:bg-amber-100 hover:border-amber-300'
      },
      slate: {
        bg: 'bg-slate-50',
        border: 'border-slate-200',
        text: 'text-slate-600',
        hover: 'hover:bg-slate-100 hover:border-slate-300'
      }
    };

    return colorMap[color] || colorMap.slate;
  };

  // Filtrer les templates basés sur la recherche et la catégorie
  const filteredTemplates = useMemo(() => {
    let filtered = templates;

    // Filtrer par catégorie
    if (selectedCategory) {
      filtered = filtered.filter(t => t.category === selectedCategory);
    }

    // Filtrer par recherche textuelle
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(t => 
        t.title.toLowerCase().includes(query) || 
        t.description.toLowerCase().includes(query)
      );
    }

    return filtered;
  }, [templates, selectedCategory, searchQuery]);

  // Récupérer les catégories uniques
  const categories = useMemo(() => {
    const uniqueCategories = new Set(templates.map(t => t.category).filter(Boolean));
    return Array.from(uniqueCategories);
  }, [templates]);

  const categoryLabels: { [key: string]: string } = {
    redaction: 'Rédaction',
    infographie: 'Infographie',
    visuel: 'Visuel',
    presentation: 'Présentation',
    video: 'Vidéo',
    audio: 'Audio'
  };

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Rédaction Libre Assistée
          </h1>
          <p className="text-gray-600">
            Choisissez un modèle ou créez votre contenu librement avec l'assistance de l'IA
          </p>
        </div>

        {loading ? (
          <div className="flex justify-center items-center py-20">
            <LoadingSpinner size="lg" />
          </div>
        ) : (
          <>
            {/* Section de recherche et filtres */}
            <div className="mb-8 space-y-4">
              {/* Barre de recherche */}
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Search className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Rechercher un modèle..."
                  className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>

              {/* Filtres par catégorie */}
              {categories.length > 0 && (
                <div className="flex items-center gap-2 flex-wrap">
                  <Filter className="h-5 w-5 text-gray-500" />
                  <button
                    onClick={() => setSelectedCategory(null)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 ${
                      selectedCategory === null
                        ? 'bg-primary-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    Tous
                  </button>
                  {categories.map((category) => (
                    <button
                      key={category}
                      onClick={() => setSelectedCategory(category === selectedCategory ? null : category)}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 ${
                        selectedCategory === category
                          ? 'bg-primary-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {categoryLabels[category] || category}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Résultats */}
            {filteredTemplates.length === 0 ? (
              <div className="text-center py-12">
                <div className="text-gray-400 mb-4">
                  <Search className="h-16 w-16 mx-auto" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Aucun résultat trouvé
                </h3>
                <p className="text-gray-600 mb-4">
                  {searchQuery ? `Aucun modèle ne correspond à "${searchQuery}"` : "Aucun modèle dans cette catégorie"}
                </p>
                {(searchQuery || selectedCategory) && (
                  <button
                    onClick={() => {
                      setSearchQuery('');
                      setSelectedCategory(null);
                    }}
                    className="text-primary-600 hover:text-primary-700 font-medium"
                  >
                    Réinitialiser les filtres
                  </button>
                )}
              </div>
            ) : (
              <>
                {/* Templates Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {filteredTemplates.map((template) => {
                    const colors = getColorClasses(template.color);
                    // Utiliser l'icône de l'API ou une icône par défaut
                    const Icon = template.icon_name && iconMap[template.icon_name] 
                      ? iconMap[template.icon_name] 
                      : FileText;
                
                    return (
                      <div 
                        key={template.id}
                        onClick={() => ouvrirUseCase(template, navigate)}
                      >
                        <Card 
                          className={`${colors.bg} ${colors.border} border-2 transition-all duration-200 ${colors.hover} cursor-pointer`}
                          padding="none"
                        >
                          {/* Image ou icône par défaut */}
                          {(template.image_url || template.image) ? (
                            <div className="relative w-full h-48 overflow-hidden rounded-t-lg">
                              <img 
                                src={template.image_url || template.image} 
                                alt={template.title}
                                className="w-full h-full object-cover"
                              />
                            </div>
                          ) : (
                            <div className={`w-full h-48 ${colors.bg} flex items-center justify-center rounded-t-lg border-b-2 ${colors.border}`}>
                              <Icon className={`w-16 h-16 ${colors.text} opacity-50`} />
                            </div>
                          )}
                          
                          {/* Contenu du card */}
                          <div className="p-6">
                            <h3 className={`${colors.text} text-lg font-semibold mb-2`}>
                              {template.title}
                            </h3>
                            <p className="text-gray-600 text-sm">
                              {template.summary || template.description}
                            </p>
                          </div>
                        </Card>
                      </div>
                    );
                  })}
                </div>

                {/* Info Section */}
                <div className="mt-12 bg-gradient-to-r from-primary-50 to-purple-50 rounded-lg p-6 border border-primary-100">
                  <h2 className="text-xl font-semibold text-gray-900 mb-3">
                    💡 Comment ça fonctionne ?
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="flex items-start">
                      <div className="bg-primary-600 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold mr-3 flex-shrink-0">
                        1
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900 mb-1">Choisissez un modèle</h3>
                        <p className="text-sm text-gray-600">
                          Sélectionnez le type de contenu que vous souhaitez créer
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <div className="bg-primary-600 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold mr-3 flex-shrink-0">
                        2
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900 mb-1">Personnalisez</h3>
                        <p className="text-sm text-gray-600">
                          Remplissez les informations ou décrivez votre besoin
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <div className="bg-primary-600 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold mr-3 flex-shrink-0">
                        3
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900 mb-1">Génération IA</h3>
                        <p className="text-sm text-gray-600">
                          L'IA crée votre contenu que vous pouvez éditer et affiner
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </DashboardLayout>
  );
};

export default FreeAssistedPage;

